import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Send, Server, Mail, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import XmlMapper from "./XmlMapper";

const ALL_FORMATS = ["word", "pdf", "text", "csv", "xlsx", "xls", "xml"];
const FMT_LABEL = { word: "Word .docx", pdf: "PDF", text: "fmt_text", csv: "CSV", xlsx: "Excel .xlsx", xls: "Excel .xls", xml: "XML" };
const DEST_TYPES = ["mail", "ftp", "sftp", "ftps"];

function SectionCard({ title, children, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-4" data-testid={testid}>
      <h3 className="font-display text-base font-semibold tracking-tight text-foreground">{title}</h3>
      {children}
    </div>
  );
}

export default function Accounting() {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/interfaces/accounting/config"); setCfg(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="p-6 text-sm text-muted-foreground">…</div>;

  const set = (patch) => setCfg({ ...cfg, ...patch });
  const toggleFmt = (f) => set({ formats: cfg.formats.includes(f) ? cfg.formats.filter((x) => x !== f) : [...cfg.formats, f] });
  const addDest = (type) => set({ destinations: [...cfg.destinations, { id: (crypto.randomUUID?.() || String(Date.now() + Math.random())), type, recipient: "", sender: "", description: "", host: "", port: type === "mail" ? "" : (type === "sftp" ? 22 : 21), path: "", user: "", password: "" }] });
  const setDest = (i, patch) => set({ destinations: cfg.destinations.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  const delDest = (i) => set({ destinations: cfg.destinations.filter((_, j) => j !== i) });

  const save = async () => {
    setBusy(true);
    try { await api.put("/interfaces/accounting/config", cfg); toast.success(t("saved")); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/interfaces/accounting/run-now"); const ok = (data.results || []).filter((r) => r.ok).length; toast.success(`${t("acc_sent_now")} (${ok}/${(data.results || []).length})`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-6 max-w-[1200px]" data-testid="accounting-config">
      {/* Sticky actiebalk */}
      <div className="sticky top-4 z-10 flex items-center justify-between gap-3 rounded-2xl border border-border bg-card/95 backdrop-blur p-4 shadow-sm">
        <p className="text-sm text-muted-foreground hidden sm:block">{t("acc_automate")}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNow} disabled={busy} data-testid="cfg-run-now"><Send className="h-4 w-4 mr-1.5" />{t("acc_run_now")}</Button>
          <Button onClick={save} disabled={busy} data-testid="cfg-save"><Save className="h-4 w-4 mr-1.5" />{t("save")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Links — configuratie */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <SectionCard title={t("acc_what_to_send")} testid="cfg-what">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">{t("acc_format")}</p>
              <div className="flex flex-wrap gap-2">
                {ALL_FORMATS.map((f) => {
                  const on = cfg.formats.includes(f);
                  return (
                    <button key={f} type="button" onClick={() => toggleFmt(f)} data-testid={`cfg-fmt-${f}`} aria-pressed={on}
                      className={`rounded-full border px-3.5 h-8 text-xs font-medium transition-all active:scale-95 ${on
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>
                      {FMT_LABEL[f].startsWith("fmt_") ? t(FMT_LABEL[f]) : FMT_LABEL[f]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_csv_sep")}</Label>
              <Input value={cfg.csv_separator} onChange={(e) => set({ csv_separator: e.target.value })} className="h-9 w-20 text-center font-mono" data-testid="cfg-sep" />
            </div>
          </SectionCard>

          <SectionCard title={t("acc_when")} testid="cfg-when">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_selection")}</Label>
                <Select value={cfg.scope} onValueChange={(v) => set({ scope: v })}><SelectTrigger data-testid="cfg-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>{["unsent", "week", "quarter", "year"].map((s) => <SelectItem key={s} value={s}>{t(`acc_scope_${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_trigger")}</Label>
                <Select value={cfg.trigger} onValueChange={(v) => set({ trigger: v })}><SelectTrigger data-testid="cfg-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>{["manual", "daily", "weekly", "quarterly"].map((s) => <SelectItem key={s} value={s}>{t(`acc_trigger_${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Rechts — bestemmingen */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-display text-base font-semibold tracking-tight text-foreground">{t("acc_destinations")}</p>
            <div className="flex flex-wrap gap-1.5">
              {DEST_TYPES.map((d) => (
                <Button key={d} size="sm" variant="outline" onClick={() => addDest(d)} data-testid={`add-dest-${d}`} className="rounded-full">
                  <Plus className="h-3.5 w-3.5 mr-1" />{d.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("acc_vars_hint")}</p>

          {cfg.destinations.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-border p-10 flex flex-col items-center justify-center text-center gap-3 text-muted-foreground" data-testid="dest-empty">
              <Inbox className="h-8 w-8 opacity-50" />
              <p className="text-sm">{t("acc_no_dest")}</p>
              <Button size="sm" variant="outline" onClick={() => addDest("mail")} data-testid="dest-empty-add"><Plus className="h-4 w-4 mr-1.5" />{t("acc_add_dest")}</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {cfg.destinations.map((d, i) => {
                const isMail = d.type === "mail";
                const Icon = isMail ? Mail : Server;
                return (
                  <div key={d.id || i} className="group rounded-2xl border border-border bg-card p-5 flex gap-4 items-start transition-all hover:-translate-y-px hover:shadow-sm" data-testid={`dest-${i}`}>
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isMail ? "bg-blue-500/10 text-blue-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-foreground">{d.type.toUpperCase()}</span>
                      {isMail ? (
                        <div className="grid sm:grid-cols-2 gap-2">
                          <Input placeholder={t("acc_sender")} value={d.sender || ""} onChange={(e) => setDest(i, { sender: e.target.value })} data-testid={`dest-sender-${i}`} />
                          <Input placeholder={t("acc_recipient")} value={d.recipient || ""} onChange={(e) => setDest(i, { recipient: e.target.value })} data-testid={`dest-recipient-${i}`} />
                          <Input className="sm:col-span-2" placeholder={t("acc_description")} value={d.description || ""} onChange={(e) => setDest(i, { description: e.target.value })} data-testid={`dest-desc-${i}`} />
                        </div>
                      ) : (
                        <div className="grid sm:grid-cols-2 gap-2">
                          <Input placeholder="IP / host" value={d.host || ""} onChange={(e) => setDest(i, { host: e.target.value })} data-testid={`dest-host-${i}`} />
                          <Input placeholder={t("acc_path")} value={d.path || ""} onChange={(e) => setDest(i, { path: e.target.value })} data-testid={`dest-path-${i}`} />
                          <Input placeholder={t("acc_user")} value={d.user || ""} onChange={(e) => setDest(i, { user: e.target.value })} data-testid={`dest-user-${i}`} />
                          <Input type="password" placeholder={d.has_password ? "••••••" : t("acc_password")} value={d.password || ""} onChange={(e) => setDest(i, { password: e.target.value })} data-testid={`dest-pass-${i}`} />
                          <Input type="number" placeholder="Port" value={d.port ?? ""} onChange={(e) => setDest(i, { port: e.target.value })} data-testid={`dest-port-${i}`} />
                        </div>
                      )}
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => delDest(i)} data-testid={`del-dest-${i}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {cfg.formats.includes("xml") && (
        <XmlMapper
          value={cfg.xml_config}
          docKeys={cfg.xml_doc_fields || []}
          lineKeys={cfg.xml_line_fields || []}
          onChange={(xc) => set({ xml_config: xc })}
          saveAll={save}
        />
      )}
    </div>
  );
}
